import { useState, useCallback } from 'react';
import { useCreateProject } from '../../hooks/useProjects';
import './CreateProjectModal.css';

interface CreateProjectModalProps {
  organizationId: string;
  onClose: () => void;
  onSuccess: (projectId: string) => void;
}

function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function isValidSlug(slug: string): boolean {
  return /^[a-z0-9]+(-[a-z0-9]+)*$/.test(slug);
}

export function CreateProjectModal({
  organizationId,
  onClose,
  onSuccess,
}: CreateProjectModalProps) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [slugError, setSlugError] = useState<string | null>(null);

  const createProject = useCreateProject();

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newName = e.target.value;
      setName(newName);

      // Auto-generate slug if not manually edited
      if (!slugEdited) {
        setSlug(generateSlug(newName));
        setSlugError(null);
      }
    },
    [slugEdited]
  );

  const handleSlugChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newSlug = e.target.value.toLowerCase();
    setSlug(newSlug);
    setSlugEdited(true);

    // Validate slug format
    if (newSlug && !isValidSlug(newSlug)) {
      setSlugError('Slug can only contain lowercase letters, numbers, and hyphens');
    } else {
      setSlugError(null);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) return;
    if (slugError) return;

    try {
      const project = await createProject.mutateAsync({
        organizationId,
        data: {
          name: name.trim(),
          slug: slug.trim() || undefined,
          description: description.trim() || undefined,
        },
      });
      onSuccess(project.id);
    } catch {
      // Error is handled by the mutation
    }
  };

  const canSubmit = name.trim() && !slugError && !createProject.isPending;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <header className="modal__header">
          <h2>Create Project</h2>
          <button className="modal__close" onClick={onClose} type="button">
            &times;
          </button>
        </header>

        <form onSubmit={handleSubmit}>
          <div className="modal__body">
            <div className="form-group">
              <label htmlFor="name">Project Name</label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={handleNameChange}
                placeholder="My App"
                autoFocus
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="slug">
                URL Slug
                <span className="label-hint">
                  Used in URLs and API endpoints
                </span>
              </label>
              <div className="slug-input">
                <span className="slug-prefix">/</span>
                <input
                  id="slug"
                  type="text"
                  value={slug}
                  onChange={handleSlugChange}
                  placeholder="my-app"
                />
              </div>
              {slugError && <p className="form-error">{slugError}</p>}
            </div>

            <div className="form-group">
              <label htmlFor="description">
                Description
                <span className="label-hint">(optional)</span>
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="A brief description of this project"
                rows={3}
              />
            </div>

            {createProject.isError && (
              <p className="form-error">
                {createProject.error?.message || 'Failed to create project'}
              </p>
            )}
          </div>

          <footer className="modal__footer">
            <button
              type="button"
              className="btn btn--secondary"
              onClick={onClose}
              disabled={createProject.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={!canSubmit}
            >
              {createProject.isPending ? 'Creating...' : 'Create Project'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}
